import { useEffect, useMemo, useState } from 'react';
import PropTypes from 'prop-types';
import { Table, Input } from 'reactstrap';
import Text from './Text';
import AppSpinner from './AppSpinner';
import AppPagination from './AppPagination';
import { formatTableDateTime, formatTableTime } from '../../constants/time';

const getColumnLabel = (column) => `${column.key} ${typeof column.header === 'string' ? column.header : ''}`;
const isDateTimeColumn = (column) => {
  const label = getColumnLabel(column);
  return !/time\s*zone|timezone/i.test(label) && /date|time/i.test(label);
};

// server mode: fetchData({page, pageSize, search, sortBy, sortDir}) -> {rows, total}
// fetchData must be stable (wrap in useCallback in the caller) or it re-fires every render.
// column.render receives (row, globalIndex) — globalIndex accounts for the current page/sort, for S.no-style columns.
// enabled=false (server mode only) skips the fetch entirely — for "pick filters, then Submit" pages that shouldn't load on mount.
const DataTable = ({
  columns,
  data,
  fetchData,
  mode,
  pageSize,
  searchable,
  searchPlaceholder,
  emptyMessage,
  rowKey,
  toolbar,
  className,
  defaultSort,
  enabled,
  rowStyle,
  rowClassName,
  renderRow,
  loading: externalLoading,
  showPagination,
  alwaysShowPagination,
}) => {
  const [page, setPage] = useState(1);
  const [search, setSearch] = useState('');
  const [sort, setSort] = useState(defaultSort || null);
  const [serverRows, setServerRows] = useState([]);
  const [serverTotal, setServerTotal] = useState(0);
  const [loading, setLoading] = useState(false);
  const isLoading = loading || externalLoading;

  useEffect(() => {
    setPage(1);
  }, [search, sort]);

  useEffect(() => {
    if (mode !== 'server' || !enabled) return undefined;
    let cancelled = false;
    setLoading(true);
    Promise.resolve(
      fetchData({ page, pageSize, search, sortBy: sort?.key, sortDir: sort?.dir }),
    )
      .then((result) => {
        if (cancelled) return;
        const rows = Array.isArray(result?.rows) ? result.rows : [];
setServerRows(rows);
setServerTotal(result?.total ?? rows.length);
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, [mode, enabled, fetchData, page, pageSize, search, sort]);

  const clientRows = useMemo(() => {
    if (mode !== 'client') return [];
   let result = Array.isArray(data) ? data : [];
    if (search) {
      const q = search.toLowerCase();
      result = result.filter((row) =>
        columns.some((col) => {
          const value = col.searchValue ? col.searchValue(row) : row[col.key];
          return value != null && String(value).toLowerCase().includes(q);
        }),
      );
    }
    if (sort?.key) {
      const col = columns.find((c) => c.key === sort.key);
      result = [...result].sort((a, b) => {
        const av = col?.sortValue ? col.sortValue(a) : a[sort.key];
        const bv = col?.sortValue ? col.sortValue(b) : b[sort.key];
        if (av == null) return 1;
        if (bv == null) return -1;
        if (av < bv) return sort.dir === 'asc' ? -1 : 1;
        if (av > bv) return sort.dir === 'asc' ? 1 : -1;
        return 0;
      });
    }
    return result;
  }, [mode, data, search, sort, columns]);

  const total = mode === 'client' ? clientRows.length : serverTotal;
  const pageCount = Math.max(1, Math.ceil(total / pageSize));
  const pageRows =
  mode === 'client'
    ? clientRows.slice((page - 1) * pageSize, page * pageSize)
    : Array.isArray(serverRows)
      ? serverRows
      : [];

  const toggleSort = (colKey) => {
    setSort((prev) => {
      if (!prev || prev.key !== colKey) return { key: colKey, dir: 'asc' };
      if (prev.dir === 'asc') return { key: colKey, dir: 'desc' };
      return null;
    });
  };

  return (
    <div className={className}>
      {(searchable || toolbar) && (
        <div className="d-flex flex-wrap justify-content-end align-items-center gap-2 mb-3 pt-3">
          {searchable ? (
            <Input
              type="search"
              bsSize="sm"
              placeholder={searchPlaceholder}
              value={search}
              onChange={(e) => setSearch(e.target.value)}
               className="data-table-search"
            />
          ) : (
            <span />
          )}
          {toolbar}
        </div>
      )}

      <div className="table-responsive">
        <Table className="custom-table no-wrap align-middle mb-0">
          <thead style={{ borderBottom: 'none', boxShadow: 'none' }}>
            <tr style={{ borderBottom: 'none', boxShadow: 'none' }}>
              {columns.map((col) => (
                <th
                  key={col.key}
                  style={{
                    width: col.width,
                    cursor: col.sortable ? 'pointer' : undefined,
                    borderBottom: 'none',
                    boxShadow: 'none',
                  }}
                  onClick={col.sortable ? () => toggleSort(col.key) : undefined}
                >
                  <Text variant="tableHeader" tag="span">
                    {col.header}
                    {col.sortable && sort?.key === col.key ? (sort.dir === 'asc' ? ' ▲' : ' ▼') : ''}
                  </Text>
                </th>
              ))}
            </tr>
          </thead>
          <tbody>
            {isLoading ? (
              <tr>
                <td colSpan={columns.length} className="text-center py-4">
                  <div className="d-flex justify-content-center">
                    <AppSpinner size="inline" label="Loading..." />
                  </div>
                </td>
              </tr>
            ) : pageRows.length === 0 ? (
              <tr>
                <td colSpan={columns.length} className="text-center py-4">
                  <Text variant="muted">{emptyMessage}</Text>
                </td>
              </tr>
            ) : (
              pageRows.map((row, idx) => {
                const globalIndex = (page - 1) * pageSize + idx;
                if (renderRow) return renderRow(row, globalIndex);
                return (
                  <tr
                    key={rowKey ? rowKey(row) : idx}
                    style={rowStyle ? rowStyle(row) : undefined}
                    className={rowClassName ? rowClassName(row) : undefined}
                  >
                    {columns.map((col) => (
                      <td
                        key={col.key}
                        className={col.className}
                        style={isDateTimeColumn(col) ? { whiteSpace: 'nowrap' } : undefined}
                      >
                        {col.render ? (
                          col.render(row, globalIndex)
                        ) : (
                          <Text variant="tableCell" tag="span">
                            {/date.?time|date\s*\/\s*time|created[_ ]*at|updated[_ ]*at|date/i.test(getColumnLabel(col))
                              ? formatTableDateTime(row[col.key])
                              : /time/i.test(getColumnLabel(col)) && !/duration|estimated|time\s*zone|timezone/i.test(getColumnLabel(col))
                                ? formatTableTime(row[col.key])
                                : row[col.key]}
                          </Text>
                        )}
                      </td>
                    ))}
                  </tr>
                );
              })
            )}
          </tbody>
        </Table>
      </div>

      {showPagination && total > 0 && (
        <div className="d-flex justify-content-end mt-3">
          <AppPagination
            page={page}
            pageCount={pageCount}
            onPageChange={setPage}
            showSinglePage={alwaysShowPagination}
          />
        </div>
      )}
    </div>
  );
};

DataTable.propTypes = {
  columns: PropTypes.arrayOf(
    PropTypes.shape({
      key: PropTypes.string.isRequired,
      header: PropTypes.node,
      render: PropTypes.func,
      sortable: PropTypes.bool,
      sortValue: PropTypes.func,
      searchValue: PropTypes.func,
      width: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
      className: PropTypes.string,
    }),
  ).isRequired,
  data: PropTypes.array,
  fetchData: PropTypes.func,
  mode: PropTypes.oneOf(['client', 'server']),
  pageSize: PropTypes.number,
  searchable: PropTypes.bool,
  searchPlaceholder: PropTypes.string,
  emptyMessage: PropTypes.string,
  rowKey: PropTypes.func,
  toolbar: PropTypes.node,
  className: PropTypes.string,
  defaultSort: PropTypes.shape({ key: PropTypes.string, dir: PropTypes.oneOf(['asc', 'desc']) }),
  enabled: PropTypes.bool,
  rowStyle: PropTypes.func,
  rowClassName: PropTypes.func,
  renderRow: PropTypes.func,
  loading: PropTypes.bool,
  showPagination: PropTypes.bool,
  alwaysShowPagination: PropTypes.bool,
};

DataTable.defaultProps = {
  data: [],
  fetchData: undefined,
  mode: 'client',
  pageSize: 10,
  searchable: true,
  searchPlaceholder: 'Search...',
  emptyMessage: 'No records found',
  rowKey: undefined,
  toolbar: null,
  className: '',
  defaultSort: null,
  enabled: true,
  rowStyle: undefined,
  rowClassName: undefined,
  renderRow: undefined,
  loading: false,
  showPagination: true,
  alwaysShowPagination: false,
};

export default DataTable;
